/* eslint-disable react/no-danger */
import "./styles.scss";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { BsChevronDown, BsDownload, BsEnvelopeFill, BsHeadset, BsPlus } from "react-icons/bs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, Checkbox } from "@nextui-org/react";
import { FaChevronLeft, FaChevronRight, FaRegUser } from "react-icons/fa";
import {
  MdOutlinePhoneCallback,
  MdOutlinePhoneForwarded,
  MdOutlineStarBorder,
  MdMailOutline,
  MdFormatListBulleted,
  MdOutlineSimCardDownload,
} from "react-icons/md";
import { CgExport } from "react-icons/cg";
import { useDispatch, useSelector } from "react-redux";

import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";
import MAudioPlayer from "../../components/MAudioPlayer";
import AStarGradeDisplay from "../../components/Kit/AStarGradeDisplay";

import { useAppContext } from "../../contexts/AppContext";
import getConfig from "../../conf/config";
import { auth } from "../../conf/firebase";
import myAxios from "../../utils/axiosWrapper";
import { downloadCallAudioBlob } from "../../utils/downloadCallAudio";
import MFloatingComponent from "../../components/Kit/MFloatingComponent";
import { fetchLists } from "../../redux/actions/listsActions";
import { MagicWandIcon } from "../../assets/icons/audio-icons";

const LiveContentModalContent = ({ item, handleOnClose = () => {}, disablePlayer = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { currentUser } = auth;

  const transcriptWrapperRef = useRef(null);
  const { debouncedSearchText: searchText } = useAppContext();
  const [hasError, setHasError] = useState(false);
  const [searchInput, setSearchInput] = useState(searchText);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isFloatingMenuDisplayed, setIsFloatingMenuDisplayed] = useState(false);
  const [inputName, setInputName] = useState("");
  const [selectedListsIds, setSelectedListsIds] = useState([]);
  const { user } = useSelector((state) => state.user);
  const { lists, loading } = useSelector((state) => state.lists.lists);
  const activeLists = lists.filter((list) => !list.is_archived && !list.is_deleted);

  let { id: itemId } = item;
  let displayableId = itemId ? `...${itemId.substring(itemId.length - 6)}` : "";

  const nbHits =
    searchInput?.length <= 0
      ? 0
      : item?.transcript?.toLowerCase()?.split(searchInput?.toLowerCase())?.length - 1;

  const isAudioUrlValid = useMemo(() => {
    if (!item?.call_timestamp) return null;

    const today = new Date();
    const callDate = new Date(item.call_timestamp * 1000);

    // Calculate the difference in time
    const diffInTime = today.getTime() - callDate.getTime();

    // Calculate the difference in days
    const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24));

    return diffInDays < 30;
  }, [item]);

  const formatAudioFileName = (item, fileExtension = "wav") => {
    const dateBloc = new Date(item.call_timestamp * 1000)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `${item?.operator_name.toUpperCase()}_${dateBloc}.${fileExtension}`;
  };

  const downloadAudioFile = async (callId) => downloadCallAudioBlob(callId);

  const onShareAudio = (item) => {
    if (!item) {
      return;
    }

    const baseUrl = getConfig().BASE_URL;
    const callLink = `${baseUrl}/calls/${item?.id}`;

    const mailtoLink = `mailto:?subject=Partage de Call Audio&body=${encodeURIComponent(
      `Voici le lien vers le call :
  ${callLink}`
    )}`;
    window.open(mailtoLink, "_blank");
  };

  const onUpdateFocusedIndex = (diff) => () => {
    if (searchInput?.length <= 0) {
      return;
    }

    const newHitIdx = (focusedIndex + diff + nbHits) % nbHits;
    setFocusedIndex(newHitIdx);

    if (!transcriptWrapperRef.current) {
      return;
    }

    const elements = transcriptWrapperRef.current.querySelectorAll("em");

    if (elements?.length <= newHitIdx) {
      return;
    }
    const targetElement = elements[newHitIdx];
    const targetElementOffsetTop = targetElement.offsetTop;
    transcriptWrapperRef.current.scrollTo({
      top: targetElementOffsetTop,
      behavior: "smooth",
      // block: "center",
    });
  };

  const exportAudioXlsx = async (item) => {
    if (!item) {
      return;
    }

    const fileName = formatAudioFileName(item, "xlsx");
    const baseUrl = getConfig().BASE_URL;
    const audioUrl = item?.audio_url;
    const callLink = `${baseUrl}/calls/${item?.id}`;

    const rows = [
      {
        id: item.id,
        audio_dl: audioUrl,
        call_link: callLink,
        operator_name: item?.operator_name || "",
        client_number: item?.client_number || "",
        call_summary: item?.analysis?.call_summary || "",
        from: item.incoming ? "incoming" : "outgoing",
        grade: item?.analysis?.call_overall_grade?.toFixed(1) || -1,
        dynamism: item?.analysis?.dynamism || -1,
        call_structure_respect: item?.analysis?.call_structure_respect || -1,
        client_sentiment: item?.analysis?.client_sentiment || "",
        customer_service_tone: item?.analysis?.customer_service_tone || "",
        team: item?.team || "",
        department: item?.department || "",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Call");
    XLSX.writeFile(workbook, fileName, { compression: false });
  };

  const audioUrlDownload = async (audio_url, filename) => {
    if (!audio_url || !isAudioUrlValid || hasError) {
      sethasError(true); // Set hasError to true if download is not possible
      return;
    }

    const audioBlob = await downloadAudioFile(item?.id);
    saveAs(audioBlob, filename);
  };

  const dispatchFetchLists = () => {
    dispatch(fetchLists());
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }

    dispatchFetchLists();
  }, [user]);

  const onRemoveSelection = (list) => () => {
    let newSelectedListIds = [...selectedListsIds];
    newSelectedListIds = newSelectedListIds.filter((listId) => listId !== list.id);
    setSelectedListsIds(newSelectedListIds);
  };

  const onAddSelection = (list) => () => {
    const newSelectedListIds = [...selectedListsIds];
    newSelectedListIds.push(list.id);
    setSelectedListsIds(newSelectedListIds);
  };

  const onCreateList = async () => {
    if (inputName.length <= 0) {
      return;
    }

    const response = await myAxios.post(`${getConfig().API_URL}/audio/api/v1/lists`, {
      list_name: inputName,
    });
    setInputName("");
    dispatchFetchLists();
  };

  const onAddCallToLists = async () => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/add_calls_lists`,
      {
        calls_ids: [item?.id],
        lists_ids: selectedListsIds,
      }
    );

    setSelectedListsIds([]);
    setIsFloatingMenuDisplayed(false);
  };

  const renderTranscript = (transcript) => {
    const isIncoming = item?.incoming;
    const splitTranscript = transcript.split("\n\n");

    return (
      <div
        className="relative live-content-search__transcript-wrapper my-4"
        ref={transcriptWrapperRef}
      >
        <div className="whitespace-pre-wrap">
          {/* Split the transcript into blocks by double new lines */}
          {splitTranscript.map((block, blockIndex) => {
            const splitBlock = block.split("\n");

            // Not formatted as expected
            if (splitBlock.length < 3) {
              return;
            }

            const timeMatch = splitBlock[1].match(
              /^(\d+)\s*(\d+:\d+:\d+,\d+)\s*-->\s*(\d+:\d+:\d+,\d+)\s*(.*)$/
            );

            // Time not formatted as expected
            if (!timeMatch) {
              return;
            }

            const startTime = splitBlock[1].substring(3, 8); // Extract only MM:SS
            const text = splitBlock[2];
            const splitText = text.split(": ");
            let displayText = text;
            let actorPicto = null;

            if (splitText.length === 2) {
              displayText = splitText[1];
              if (isIncoming) {
                actorPicto =
                  splitText[0] === "0" ? <BsHeadset size={24} /> : <FaRegUser size={24} />;
              } else {
                actorPicto =
                  splitText[0] === "0" ? <FaRegUser size={24} /> : <BsHeadset size={24} />;
              }
            }

            displayText = displayText
              .toLowerCase()
              .replace(searchInput.toLowerCase(), `<em>${searchInput}</em>`);

            return (
              <div
                key={`b-${blockIndex}`}
                className="mb-2"
              >
                <div className="flex items-center mb-1">
                  {actorPicto && <div className="mr-2">{actorPicto}</div>}
                  <div className="bg-primary text-white rounded-md px-2 py-1 mr-2">
                    <div className="text-xs">{startTime}</div>
                  </div>
                  <div
                    className="live-content-search__transcript-text text-base"
                    dangerouslySetInnerHTML={{ __html: displayText }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFLoatingMenu = () => (
    <div className="mt-1.5 bg-white rounded-lg p-2 text-[#33403F] drop-shadow-xl">
      <div className="mb-3">
        {activeLists.map((activeList) => {
          const isListSelected = selectedListsIds.includes(activeList.id);

          return (
            <div className="flex items-center py-1.5">
              <Checkbox
                isSelected={isListSelected}
                onValueChange={
                  isListSelected ? onRemoveSelection(activeList) : onAddSelection(activeList)
                }
              />
              <span className="ml-1 text-sm">{activeList.name}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center mb-2 text-sm">
        <BsPlus size={24} />
        <span className="whitespace-nowrap ml-1 mr-4">{t("Create new list")}</span>
        <BsChevronDown
          style={{ marginLeft: "auto" }}
          size={16}
        />
      </div>
      <div className="flex items-center rounded-full border-2 border-[#E5E5E5] border-solid bg-white pl-2 text-sm">
        <input
          className="outline-none	border-0"
          placeholder="Name"
          value={inputName}
          onChange={(e) => setInputName(e.target.value)}
        />
        <div
          onClick={onCreateList}
          onKeyDown={onCreateList}
          className="cursor-pointer rounded-full border-2 border-[#E5E5E5] border-solid px-3 py-2"
          style={{ margin: "-2px" }}
        >
          {t("Create")}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Card>
        <CardBody className="px-4 px-8">
          <div className="flex justify-between mb-4">
            <div>
              <h1 className="text-4xl mt-3 text-primary font-semibold capitalize">
                {item?.operator_name}
              </h1>
              <CustomCopyToClipboard
                copyText={itemId}
                displayText={displayableId}
                displayClassName="text-sm"
              />
            </div>
            <div className="flex items-start max-h-[30px]">
              <div className="text-xs text-[#171717] bg-white rounded-md px-[8px] py-[4px] flex items-center border-1 border-[#E5E5E5] border-solid">
                {item.incoming ? (
                  <MdOutlinePhoneCallback
                    size={20}
                    color="#171717"
                    className="mr-2"
                  />
                ) : (
                  <MdOutlinePhoneForwarded
                    size={20}
                    color="#171717"
                    className="mr-2"
                  />
                )}
                <span>{item?.incoming ? "Incoming" : "Outgoing"}</span>
              </div>
              <div className="w-full flex justify-start items-center ml-[8px]">
                <AStarGradeDisplay grade={item?.analysis?.call_overall_grade} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end mb-4">
            <div className="flex-col items-end justify-center">
              <div className="flex items-center rounded-md border-1 border-[#E5E5E5] border-solid bg-white pl-2 text-sm">
                <div className="mr-[4px]">
                  <MagicWandIcon />
                </div>
                <input
                  className="outline-none	border-0"
                  placeholder="Search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <div
                  onClick={onUpdateFocusedIndex(-1)}
                  onKeyDown={onUpdateFocusedIndex(-1)}
                  className="cursor-pointer hover:bg-black/10 rounded-md border-l-1 border-[#E5E5E5] border-solid p-[8px]"
                >
                  <FaChevronLeft color="#737373" />
                </div>
                <div
                  onClick={onUpdateFocusedIndex(1)}
                  onKeyDown={onUpdateFocusedIndex(1)}
                  className="cursor-pointer hover:bg-black/10 border-l-1 border-[#E5E5E5] border-solid p-[8px]"
                >
                  <FaChevronRight color="#737373" />
                </div>
              </div>
              <div className="text-end text-sm text-[#404040]/[0.6] mt-[4px]">{`${Math.min(
                nbHits,
                focusedIndex + 1
              )}/${nbHits} results`}</div>
            </div>
          </div>

          <div className="relative h-[70vh]">
            {item?.transcript?.length > 0 && renderTranscript(item?.transcript)}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex items-center rounded-md border-1 border-[#E5E5E5] border-solid shadow-drop-100">
                  <div className="border-r-1 border-r-[#E5E5E5] border-r-solid">
                    <div
                      onClick={() => onShareAudio(item)}
                      onKeyDown={() => onShareAudio(item)}
                      className="cursor-pointer hover:bg-black/10 duration-400 h-[40px] px-[12px] rounded-md flex items-center text-sm text-[#171717]"
                    >
                      {hasError ? t("Impossible to share") : t("Share")}
                      <MdMailOutline
                        size={20}
                        className="ml-2"
                      />
                    </div>
                  </div>
                  <div className="border-r-1 border-r-[#E5E5E5] border-r-solid">
                    <div
                      onClick={() => audioUrlDownload(item?.audio_url, formatAudioFileName(item))}
                      onKeyDown={() => audioUrlDownload(item?.audio_url, formatAudioFileName(item))}
                      className="cursor-pointer hover:bg-black/10 duration-400 h-[40px] px-[12px] rounded-md flex items-center text-sm text-[#171717]"
                    >
                      {hasError ? t("Audio unavailable") : t("Download")}
                      <MdOutlineSimCardDownload
                        size={20}
                        color="#737373"
                        className="ml-2"
                      />
                    </div>
                  </div>
                  <MFloatingComponent
                    targetElement={
                      <div
                        onClick={() => {
                          setIsFloatingMenuDisplayed(!isFloatingMenuDisplayed);
                        }}
                        onKeyDown={() => {
                          setIsFloatingMenuDisplayed(!isFloatingMenuDisplayed);
                        }}
                        className="cursor-pointer hover:bg-black/10 duration-400 h-[40px] px-[12px] rounded-md flex items-center text-sm text-[#171717]"
                      >
                        {t("List")}
                        <MdFormatListBulleted
                          size={20}
                          className="ml-2"
                        />
                      </div>
                    }
                    isTopPositioned
                    floatingElement={renderFLoatingMenu()}
                    isFloatingDisplayed={isFloatingMenuDisplayed}
                    setIsFloatingDisplayed={setIsFloatingMenuDisplayed}
                  />
                </div>
                {selectedListsIds?.length > 0 && (
                  <Button
                    className="ml-2 text-sm"
                    size="sm"
                    color="primary"
                    radius="full"
                    onPress={() => onAddCallToLists()}
                  >
                    <div>{t("Add")}</div>
                  </Button>
                )}
              </div>
              {!disablePlayer && (
                <MAudioPlayer
                  audioUrlForLocalState={item.audio_url}
                  autoPlay={false}
                  mode="live"
                  className="shadow-none"
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LiveContentModalContent;
